import * as React from 'react'

import { Box, CollapsableCard, Icon, Tag, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import * as styles from 'modules/billing/components/FactorialF25Bundles/index.vanilla.css'
import { formatPrice } from 'modules/billing/lib/prices'
import { useF25PricingContext } from 'modules/onboarding/components/Pricing/F25PricingContext'
import { capitalizeFirstLetter } from 'modules/onboarding/lib/pricing/plans/utils'
import { Tier } from 'modules/onboarding/lib/pricing/types'

type Props = {
  currency: string
}

type OrderItem = {
  title: string
  isMainPlan: boolean
  perSeatPrice?: number
  feePrice?: number
  tieredPrice?: Tier[]
  stairstepPrice?: number
  country: string
}

const OrderItem = ({
  title,
  isMainPlan,
  perSeatPrice,
  feePrice,
  tieredPrice,
  stairstepPrice,
  currency,
  country,
}: OrderItem & Props) => {
  const { minSeatsToCharge, isSelfServedSegment } = useF25PricingContext()
  const mainPlanPrice = perSeatPrice ? perSeatPrice * minSeatsToCharge : feePrice || stairstepPrice
  const price = perSeatPrice ? perSeatPrice * minSeatsToCharge : feePrice || stairstepPrice
  const totalPrice = isMainPlan ? mainPlanPrice : price
  // The first tier of a plan have a price per seat of 0. We can take the endingUnit of this tier to figure out the starting seats.
  const includedSeatsPerModule = tieredPrice?.[0]?.endingUnit
  // The next tier is where we can find the price per seat of all those seats above the "starting seats".
  const pricePerUser = tieredPrice?.[1]?.price || 0

  const EXCLUDED_COUNTRIES = ['br']
  const excludedCountries = EXCLUDED_COUNTRIES.includes(country)

  return (
    <Box gap="s8">
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Text size="200" color="grey1000" weight="semibold">
          {title}
        </Text>
        <Text
          size="200"
          color="grey1000"
          weight="semibold"
          className={isSelfServedSegment && !excludedCountries ? '' : styles.blurredText}
        >
          {formatPrice(totalPrice || 0, currency)}
        </Text>
      </Box>
      {perSeatPrice ? (
        <Text size="200" weight="normal" className={excludedCountries ? styles.blurredText : ''}>
          {/* We do this to avoid showing the price per seat to selfserved companies */}
          {isMainPlan && isSelfServedSegment ? (
            <>{capitalizeFirstLetter(i18n.t('pricing_plans.fixed_fee'))}</>
          ) : (
            <>
              {formatPrice(perSeatPrice, currency)} {i18n.t('pricing_plans.per_employee_month')}
            </>
          )}
        </Text>
      ) : null}
      {feePrice ? (
        <Text size="200" weight="normal" className={excludedCountries ? styles.blurredText : ''}>
          {capitalizeFirstLetter(i18n.t('pricing_plans.fixed_fee'))}
          {includedSeatsPerModule && (
            <>
              {`, ${i18n.t('pricing_plans.employees_inludede', {
                employees: includedSeatsPerModule,
              })}`}
            </>
          )}
        </Text>
      ) : null}
      {stairstepPrice ? (
        <Text size="200" weight="normal" className={excludedCountries ? styles.blurredText : ''}>
          {capitalizeFirstLetter(i18n.t('pricing_plans.fixed_fee'))}
        </Text>
      ) : null}
      {tieredPrice ? (
        <Text.Normal size="200" className={excludedCountries ? styles.blurredText : ''}>
          {formatPrice(pricePerUser / 100, currency)} {i18n.t('pricing_plans.per_employee_month')}
        </Text.Normal>
      ) : null}
    </Box>
  )
}

const OrderSummary = ({ currency }: Props) => {
  const { allPlans, period, country } = useF25PricingContext()
  const [isOpen, setIsOpen] = React.useState(false)

  const getOrderSummary = (): OrderItem[] => {
    return allPlans.f25Plans
      .concat(allPlans.f25Addons)
      .filter((plan) => plan.isSelected)
      .map((plan) => {
        return {
          title: plan.title,
          isMainPlan: !!plan.includedAddons?.length,
          perSeatPrice: plan.pricing.getSeatsPrice(period),
          feePrice: plan.pricing.getFeePrice(period),
          tieredPrice: plan.pricing.getTieredPrice(period),
          stairstepPrice: plan.pricing.getStairstepPrice(period),
          country: country,
        }
      })
  }

  return (
    <CollapsableCard
      Card={
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          onClick={() => {
            setIsOpen(!isOpen)
          }}
          cursor="pointer"
        >
          <Box flexDirection="row" gap="s6">
            <Text size="300" weight="semibold" color="grey1000">
              {i18n.t('pricing_plans.summary')}
            </Text>
            <Tag type="viridian">
              <Text size="200" color="viridian1200" weight="semibold">
                {getOrderSummary().length}
              </Text>
            </Tag>
          </Box>
          <Icon.Medium icon={isOpen ? 'chevron-up' : 'chevron-down'} />
        </Box>
      }
      Content={
        <Box gap="s24">
          {getOrderSummary().map((orderItem) => (
            <OrderItem key={orderItem.title} {...orderItem} currency={currency} country={country} />
          ))}
        </Box>
      }
      open={isOpen}
    />
  )
}

export default OrderSummary
